import { Logic } from "./evaluation";
import { isArray, isEmpty, isNil, keys, pickBy, without } from "lodash";
import { COUNTRIES } from "./countries";
import { ENTITY_TYPE_CATEGORY, isCorp, isTrust } from "./investor";
import { checkCategoryEditable } from "./kycMarkerMaintain";
import { AmlStatus } from "../constant/amlConstant";
import CategoryEditingConfirmModal from "../components/CategoryEditingConfirmModal";
import TrsExemptionDeclarationModal from "../components/TrsExemptionDeclarationModal";
export const RoleApplied = {
  INDIVIDUAL: "I",
  ENTITY: "E",
  BOTH: "B"
};

export function checkOrganisationVisibility(entityType: string) {
  return isTrust(entityType) || isCorp(entityType);
}

export const QuestionType = {
  SELECT: "SELECT",
  OPTION: "OPTION",
  HISTORIC_DATE: "DATE_HISTORIC",
  COUNTRY: "COUNTRY",
  TEXTFIELD: "TEXTFIELD",
  TEXTAREA: "TEXTAREA",
  SELECTANDADD: "SELECTANDADD",
  STATIC_TEXT: "STATIC_TEXT"
};

export const ListQuestionType = [QuestionType.SELECTANDADD];

export const LabelQuestionType = [QuestionType.STATIC_TEXT];

type InvestorCategory = { desc: string; code: string };
type InvestorSubCategory = { categoryCode: string; desc: string; code: string };
type AssociatedParty = { role: string };
type InvestorAmlDetails = { status?: string } | undefined;

export function getQuestionConfig(
  formValues: Record<string, unknown>,
  opts: {
    investorCategories?: InvestorCategory[];
    investorSubCategories?: InvestorSubCategory[];
    associatedParties?: AssociatedParty[];
    investorAmlDetails?: InvestorAmlDetails;
    permissions?: any;
  }
) {
  const {
    investorCategories = [] as InvestorCategory[],
    investorSubCategories = [] as InvestorSubCategory[],
    associatedParties = [] as AssociatedParty[],
    investorAmlDetails,
    permissions = [] as any[],
  } = opts;
  const parentCompanyAssociatedParties = associatedParties.filter((el) => el.role === "PARENTCOMPANY");
  const amlStatus = (investorAmlDetails?.status ?? AmlStatus.UNC) as (typeof AmlStatus)[keyof typeof AmlStatus];
  const categoryEditable = checkCategoryEditable(permissions, amlStatus);
  const noneEmptyKeys = getNoneEmptyKeys(formValues);
  // [HWAP-6423] if there's any question answered. Then confirm before change category or subCategory
  const needConfirmBeforeChangeCategory = !isEmpty(without(noneEmptyKeys, "QT1", "QT2"));
  return {
    "QT1": {
      show: true,
      InputProps: {
        options: investorCategories,
        fieldNames: { label: "desc", value: "code" },
        disabled: !categoryEditable,
      },
      resetOnChange: [{ qid: "QT2", value: undefined }],
      ConfirmModal: needConfirmBeforeChangeCategory ? CategoryEditingConfirmModal : null
    },
    "QT2": {
      show: true,
      InputProps: {
        options: (formValues["QT1"] as string | undefined)
          ? investorSubCategories.filter((el) => el.categoryCode === (formValues["QT1"] as string))
          : [],
        fieldNames: { label: "desc", value: "code" },
        disabled: !categoryEditable,
      },
      ConfirmModal: needConfirmBeforeChangeCategory ? CategoryEditingConfirmModal : null
    },
    "Q200": {
      show: {
        logic: Logic.IN,
        field: "QT2",
        value: ["REG01", "REG02", "REG03", "REG04", "PRIV01", "PRIV02", "PRIV03", "PRIV04", "TRUST07"]
      }
    },
    "Q201": {
      show: {
        logic: Logic.OR,
        expressions: [
          {
            logic: Logic.IN,
            field: "QT2",
            value: ["TRUST06", "TRUST04", "TRUST03", "UCLUB01", "UTRUST01", "UPRIV01", "PRIV05"]
          },
          {
            logic: Logic.EQ,
            field: "QT1",
            value: ENTITY_TYPE_CATEGORY.TRUST
          }
        ]
      },
    },
    "Q190": {
      show: {
        logic: Logic.IN,
        field: "QT2",
        value: ["REG01", "REG02", "REG03", "REG04", "PRIV01", "PRIV02", "PRIV03", "PRIV04", "TRUST07"]
      },
      InputProps: {
        options: COUNTRIES,
        fieldNames: { label: "country", value: "code" }
      }
    },
    "Q191": {
      show: {
        logic: Logic.OR,
        expressions: [
          {
            logic: Logic.IN,
            field: "QT2",
            value: ["TRUST06", "TRUST04", "TRUST03", "UCLUB01", "UTRUST01", "UPRIV01"]
          },
          {
            logic: Logic.EQ,
            field: "QT1",
            value: ENTITY_TYPE_CATEGORY.TRUST
          }
        ]
      },
      InputProps: {
        options: COUNTRIES,
        fieldNames: { label: "country", value: "code" }
      }
    },
    "Q220": {
      show: {
        logic: Logic.IN,
        field: "QT2",
        value: ["REG01", "REG02", "REG03", "REG04", "PRIV01", "PRIV02", "PRIV03", "PRIV04", "PRIV05", "TRUST07"]
      }
    },
    "Q225": {
      show: {
        logic: Logic.AND,
        expressions: [
          {
            logic: Logic.IN,
            field: "QT2",
            value: ["REG01", "REG02", "REG03", "REG04", "PRIV01", "PRIV02", "PRIV03", "PRIV04", "PRIV05", "TRUST07"]
          },
          {
            logic: Logic.EQ,
            field: "Q220",
            value: "ZZ1OTHER"
          }
        ]
      },
      InputProps: {
        maxLength: 50
      }
    },
    "Q340": {
      show: {
        logic: Logic.IN,
        field: "QT2",
        value: ["PRIV01", "PRIV02", "PRIV03", "PRIV04", "TRUST07"]
      },
      InputProps: {
        radioOptions: [{ value: "Y", label: "Yes" }, { value: "N", label: "No" }]
      }
    },
    "Q350": {
      show: {
        logic: Logic.OR,
        expressions: [
          { logic: Logic.EQ, field: "QT1", value: ENTITY_TYPE_CATEGORY.TRUST },
          { logic: Logic.EQ, field: "QT2", value: "PRIV02" }
        ]
      },
      InputProps: {
        radioOptions: [{ value: "Y", label: "Yes" }, { value: "N", label: "No" }]
      }
    },
    "Q450": {
      show: {
        logic: Logic.OR,
        expressions: [
          { logic: Logic.EQ, field: "QT1", value: ENTITY_TYPE_CATEGORY.TRUST },
          { logic: Logic.EQ, field: "QT2", value: "TRUST05" }
        ]
      }
    },
    "Q235": {
      show: {
        logic: Logic.IN,
        field: "QT2",
        value: ["PRIV01", "PRIV02", "TRUST01"]
      },
      InputProps: {
        radioOptions: [{ value: "Y", label: "Yes" }, { value: "N", label: "No" }]
      }
    },
    // -- Organisation Structure --
    "Q210": {
      show: {
        logic: Logic.IN,
        field: "QT2",
        value: ["REG01", "REG02", "REG03", "REG04"]
      },
      InputProps: {
        options: COUNTRIES,
        fieldNames: { label: "country", value: "code" }
      }
    },
    "Q110": {
      show: {
        logic: Logic.IN,
        field: "QT2",
        value: ["REG01", "REG02", "REG03", "REG04"]
      },
      InputProps: {
        disabled: parentCompanyAssociatedParties.length > 0,
        radioOptions: [{ value: "Y", label: "Yes" }, { value: "N", label: "No" }]
      },
      fixedValue: parentCompanyAssociatedParties.length > 0 ? "Y" : undefined
    },
    "Q270": {
      show: {
        logic: Logic.IN,
        field: "QT2",
        value: ["REG01", "REG02", "REG03", "REG04"]
      }
    },
    "Q280": {
      show: {
        logic: Logic.AND,
        expressions: [
          {
            logic: Logic.IN,
            field: "QT2",
            value: ["REG01", "REG02", "REG03", "REG04"]
          },
          {
            logic: Logic.IN,
            field: "Q270",
            value: ["SUBSIDIARY", "BRANCH"]
          }
        ]
      },
      InputProps: {
        options: COUNTRIES,
        fieldNames: { label: "country", value: "code" }
      }
    },
    "Q380": {
      show: {
        logic: Logic.IN,
        field: "QT2",
        value: ["TRUST06", "TRUST04", "TRUST03", "UCLUB01", "UTRUST01", "UPRIV01", "TRUST05", "PRIV05"]
      },
      InputProps: {
        options: COUNTRIES,
        fieldNames: { label: "country", value: "code" }
      }
    },
    // -- Regulatory Authority --
    "Q100": {
      show: {
        logic: Logic.IN,
        field: "QT2",
        value: ["REG01", "REG02", "REG03", "REG04"]
      },
      InputProps: {
        radioOptions: [{ value: "Y", label: "Yes" }, { value: "N", label: "No" }]
      }
    },
    "Q120": {
      show: {
        logic: Logic.AND,
        expressions: [
          { logic: Logic.IN, field: "QT2", value: ["REG01", "REG02", "REG03", "REG04"] },
          { logic: Logic.EQ, field: "Q110", value: "Y" }
        ]
      },
      InputProps: {
        radioOptions: [{ value: "Y", label: "Yes" }, { value: "N", label: "No" }]
      }
    },
    "Q180": {
      show: {
        logic: Logic.AND,
        expressions: [
          { logic: Logic.IN, field: "QT2", value: ["REG01", "REG02", "REG03", "REG04"] },
          {
            logic: Logic.OR, expressions: [
              { logic: Logic.EQ, field: "Q100", value: "Y" },
              { logic: Logic.EQ, field: "Q120", value: "Y" }
            ]
          }
        ]
      },
      InputProps: {
        options: COUNTRIES,
        fieldNames: { label: "country", value: "code" }
      }
    },
    "Q150": {
      show: {
        logic: Logic.AND,
        expressions: [
          { logic: Logic.IN, field: "QT2", value: ["REG01", "REG02", "REG03", "REG04"] },
          {
            logic: Logic.OR, expressions: [
              { logic: Logic.EQ, field: "Q100", value: "Y" },
              { logic: Logic.EQ, field: "Q120", value: "Y" }
            ]
          }
        ]
      }
    },
    "Q160": {
      show: {
        logic: Logic.AND,
        expressions: [
          { logic: Logic.IN, field: "QT2", value: ["REG01", "REG02", "REG03", "REG04"] },
          { logic: Logic.EQ, field: "Q150", value: "REGAUTH5" }
        ]
      }
    },
    "Q170": {
      show: {
        logic: Logic.AND,
        expressions: [
          { logic: Logic.IN, field: "QT2", value: ["REG01", "REG02", "REG03", "REG04"] },
          {
            logic: Logic.OR, expressions: [
              { logic: Logic.EQ, field: "Q100", value: "Y" },
              { logic: Logic.EQ, field: "Q120", value: "Y" }
            ]
          }
        ]
      }
    },
    // -- Professional Register --
    "Q255": {
      show: {
        logic: Logic.EQ,
        field: "QT2",
        value: "REG04"
      },
      InputProps: {
        radioOptions: [{ value: "Y", label: "Yes" }, { value: "N", label: "No" }]
      }
    },
    "Q260": {
      show: {
        logic: Logic.AND,
        expressions: [
          {
            logic: Logic.EQ,
            field: "QT2",
            value: "REG04"
          },
          {
            logic: Logic.EQ,
            field: "Q255",
            value: "Y"
          }
        ]
      },
      InputProps: {
        options: COUNTRIES,
        fieldNames: { label: "country", value: "code" }
      }
    },
    "Q230": {
      show: {
        logic: Logic.AND,
        expressions: [
          {
            logic: Logic.EQ,
            field: "QT2",
            value: "REG04"
          },
          {
            logic: Logic.EQ,
            field: "Q255",
            value: "Y"
          }
        ]
      }
    },
    "Q240": {
      show: {
        logic: Logic.AND,
        expressions: [
          {
            logic: Logic.EQ,
            field: "QT2",
            value: "REG04"
          },
          {
            logic: Logic.EQ,
            field: "Q230",
            value: "PROFREG8"
          }
        ]
      }
    },
    "Q250": {
      show: {
        logic: Logic.AND,
        expressions: [
          {
            logic: Logic.EQ,
            field: "QT2",
            value: "REG04"
          },
          {
            logic: Logic.EQ,
            field: "Q255",
            value: "Y"
          }
        ]
      }
    },
    // -- Company Registration --
    "Q300": {
      show: {
        logic: Logic.IN,
        field: "QT2",
        value: ["PRIV01", "PRIV02", "PRIV03", "PRIV04", "TRUST07"]
      }
    },
    "Q305": {
      show: {
        logic: Logic.AND,
        expressions: [
          {
            logic: Logic.IN,
            field: "QT2",
            value: ["PRIV01", "PRIV02", "PRIV03", "PRIV04", "TRUST07"]
          },
          {
            logic: Logic.EQ,
            field: "Q300",
            value: "COREGBDY4"
          }
        ]
      }
    },
    "Q310": {
      show: {
        logic: Logic.IN,
        field: "QT2",
        value: ["PRIV01", "PRIV02", "PRIV03", "PRIV04", "TRUST07"]
      }
    },
    // -- Shareholders --
    "Q320": {
      show: {
        logic: Logic.IN,
        field: "QT2",
        value: ["PRIV01", "PRIV02", "PRIV03", "PRIV04", "TRUST07"]
      },
      InputProps: {
        radioOptions: [{ value: "Y", label: "Yes" }, { value: "N", label: "No" }]
      }
    },
    "Q330": {
      show: {
        logic: Logic.IN,
        field: "QT2",
        value: ["PRIV01", "PRIV02", "PRIV03", "PRIV04", "TRUST07"]
      },
      InputProps: {
        radioOptions: [{ value: "Y", label: "Yes" }, { value: "N", label: "No" }]
      }
    },
    "Q355": {
      show: {
        logic: Logic.IN,
        field: "QT2",
        value: ["PRIV04", "TRUST06", "TRUST04", "UCLUB01", "UTRUST01", "UPRIV01"]
      },
      InputProps: {
        radioOptions: [{ value: "Y", label: "Yes" }, { value: "N", label: "No" }]
      }
    },
    "Q357": {
      show: {
        logic: Logic.AND,
        expressions: [
          {
            logic: Logic.IN,
            field: "QT2",
            value: ["PRIV04", "TRUST06", "TRUST04", "UCLUB01", "UTRUST01", "UPRIV01"]
          },
          {
            logic: Logic.EQ,
            field: "Q355",
            value: "Y"
          }
        ]
      }
    },
    "Q358": {
      show: {
        logic: Logic.AND,
        expressions: [
          {
            logic: Logic.IN,
            field: "QT2",
            value: ["PRIV04", "TRUST06", "TRUST04", "UCLUB01", "UTRUST01", "UPRIV01"]
          },
          {
            logic: Logic.EQ,
            field: "Q357",
            value: "CHRTYREG4"
          }
        ]
      }
    },
    "Q360": {
      show: {
        logic: Logic.AND,
        expressions: [
          {
            logic: Logic.IN,
            field: "QT2",
            value: ["PRIV04", "TRUST06", "TRUST04", "UCLUB01", "UTRUST01", "UPRIV01"]
          },
          {
            logic: Logic.EQ,
            field: "Q355",
            value: "Y"
          }
        ]
      }
    },
    // -- Pension Schemes --
    "Q400": {
      show: {
        logic: Logic.IN,
        field: "QT2",
        value: ["PEN01", "PEN02", "PEN03", "PEN04", "PEN05"]
      },
      InputProps: {
        radioOptions: [{ value: "Y", label: "Yes" }, { value: "N", label: "No" }]
      }
    },
    "Q401": {
      show: {
        logic: Logic.AND,
        expressions: [
          {
            logic: Logic.IN,
            field: "QT2",
            value: ["PEN01", "PEN02", "PEN03", "PEN04", "PEN05"]
          },
          {
            logic: Logic.EQ,
            field: "Q400",
            value: "Y"
          }
        ]
      }
    },
    "Q402": {
      show: {
        logic: Logic.AND,
        expressions: [
          {
            logic: Logic.IN,
            field: "QT2",
            value: ["PEN01", "PEN02", "PEN03", "PEN04", "PEN05"]
          },
          {
            logic: Logic.EQ,
            field: "Q401",
            value: "PENSNREG4"
          }
        ]
      }
    },
    "Q403": {
      show: {
        logic: Logic.AND,
        expressions: [
          {
            logic: Logic.IN,
            field: "QT2",
            value: ["PEN01", "PEN02", "PEN03", "PEN04", "PEN05"]
          },
          {
            logic: Logic.EQ,
            field: "Q400",
            value: "Y"
          }
        ]
      }
    },
    "Q420": {
      show: {
        logic: Logic.IN,
        field: "QT2",
        value: ["PEN01", "PEN03", "PEN04"]
      },
    },
    "Q420-1": {
      show: {
        logic: Logic.IN,
        field: "QT2",
        value: ["PEN01", "PEN03", "PEN04"]
      },
      InputProps: {
        radioOptions: [{ value: "Y", label: "Yes" }, { value: "N", label: "No" }]
      }
    },
    "Q420-2": {
      show: {
        logic: Logic.IN,
        field: "QT2",
        value: ["PEN01", "PEN03", "PEN04"]
      },
      InputProps: {
        radioOptions: [{ value: "Y", label: "Yes" }, { value: "N", label: "No" }]
      }
    },
    // -- TRS Structure --
    "G030": {
      show: {
        logic: Logic.EQ,
        field: "QT1",
        value: ENTITY_TYPE_CATEGORY.TRUST
      }
    },
    "Q510": {
      show: {
        logic: Logic.EQ,
        field: "QT1",
        value: ENTITY_TYPE_CATEGORY.TRUST
      },
      // Treat Q510 as a non-input header for the TRS group: disable validation & asterisk
      InputProps: {
        skipValidation: true,
        hideAsterisk: true
      }
    },
    "Q510A": {
      show: {
        logic: Logic.EQ,
        field: "QT1",
        value: ENTITY_TYPE_CATEGORY.TRUST
      },
      InputProps: {
        radioOptions: [{ value: "Y", label: "Yes", needConfirm: false }, { value: "N", label: "No", needConfirm: true }]
      },
      resetOnChange: [{ whereValue: "Y", qid: "Q510B", value: "N" }, { whereValue: "N", qid: "Q510B", value: "Y" }],
      ConfirmModal: TrsExemptionDeclarationModal
    },
    "Q510B": {
      show: {
        logic: Logic.EQ,
        field: "QT1",
        value: ENTITY_TYPE_CATEGORY.TRUST
      },
      InputProps: {
        radioOptions: [{ value: "Y", label: "Yes", needConfirm: true }, { value: "N", label: "No", needConfirm: false }]
      },
      resetOnChange: [{ whereValue: "Y", qid: "Q510A", value: "N" }, { whereValue: "N", qid: "Q510A", value: "Y" }],
      ConfirmModal: TrsExemptionDeclarationModal
    },
    "Q515": {
      show: {
        logic: Logic.AND,
        expressions: [
          {
            logic: Logic.EQ,
            field: "QT1",
            value: ENTITY_TYPE_CATEGORY.TRUST
          },
          {
            logic: Logic.EQ,
            field: "Q510A",
            value: "Y"
          },
          {
            logic: Logic.EQ,
            field: "Q510B",
            value: "N"
          }
        ]
      }
    },
    "Q520": {
      show: {
        logic: Logic.AND,
        expressions: [
          {
            logic: Logic.EQ,
            field: "QT1",
            value: ENTITY_TYPE_CATEGORY.TRUST
          },
          {
            logic: Logic.EQ,
            field: "Q510A",
            value: "N"
          },
          {
            logic: Logic.EQ,
            field: "Q510B",
            value: "Y"
          },
        ]
      }
    }
  };
}

function getNoneEmptyKeys(obj: Record<string, unknown>): string[] {
  return keys(pickBy(obj, (value: unknown) => {
    return !isNil(value) && !isEmpty(value);
  }));
}
